import React, { useEffect, useMemo } from 'react';

import { Handle, Position } from '@xyflow/react';

import { TimingIcon } from 'assets/icons/timing-icon';

import { timeDelaySchema } from '../../../summary-sidebar/sections/time-delay-section';
import { useWorkflowBuilderContext } from '../../../wizard/builder-context';
import { formatDelayString, formatTimeString } from '../../../wizard/helpers';
import { type TimingNodeData } from '../models';
import { AddNodeHandle } from '../shared/add-node-handle';

import { NodeActions } from './node-actions';
import { NodeRowContainer, StyledBaseNode, StyledDescription, StyledDivider, StyledLabel } from './styles';

type Props = {
  data: TimingNodeData;
  id: string;
};

export function CustomTimeDelayNode({ data, id }: Props) {
  const [builderState] = useWorkflowBuilderContext();
  const { selectedNode, isReadOnly } = builderState;
  const isCurrentlySelectedNode = selectedNode?.id === id;
  const { stats } = data;
  const waitValueLabel = formatDelayString(data.config.durationInMinutes, data.config.timeUnitId) ?? 'No wait time';

  let resumeAtLabel = 'No time preference';
  if (data.config.timeOfDayToSend) {
    resumeAtLabel = `Resume at ${formatTimeString(data.config.timeOfDayToSend)}`;
  }

  const isValid = useMemo(() => timeDelaySchema.isValidSync(data), [data]);

  useEffect(() => {
    if (data.reportIsValid) {
      data.reportIsValid(isValid);
    }
  }, [isValid, data]);

  return (
    <StyledBaseNode isError={!isValid} isSelected={isCurrentlySelectedNode}>
      <Handle position={Position.Top} type='target' />
      <NodeRowContainer>
        <TimingIcon />
        <StyledLabel>Time Delay</StyledLabel>
      </NodeRowContainer>
      <StyledDescription>{`${waitValueLabel} - ${resumeAtLabel}`}</StyledDescription>
      {!isReadOnly && <StyledDivider variant='compact' />}
      <NodeActions nodeId={id} nodeType={data.nodeType} showAnalytics={data.showAnalytics} stats={stats} />
      {data.onAddNode && <AddNodeHandle type='next' onAdd={(nodeType) => data.onAddNode!(id, nodeType)} />}
      <Handle position={Position.Bottom} type='source' />
    </StyledBaseNode>
  );
}
